/* ============================================================
   PITCH DECK CAROUSEL — section-pitch-deck-carousel.js
   Booth display: 6 full-bleed presentation slides on slow auto-advance.
   Cloned from lookbook-carousel; reuses the .lkbk- CSS for shared visual.
   Uses ?display=pitch so it fullscreens independently from the lookbook.
   ============================================================ */

(function () {
  'use strict';

  const CHAPTERS = { cta: '' };

  const SLIDES = [
    {
      type: 'product',
      chapter: '',
      bg: 'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/PitchDesk-Wedding_Presentation.jpg?v=1777829979',
      bgPosition: 'center center',
      eyebrow: '',
      heading: '',
      body: '',
      product: 'Slide 1'
    },
    {
      type: 'product',
      chapter: '',
      bg: 'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/PitchDesk-Wedding_Presentation2.jpg?v=1777829978',
      bgPosition: 'center center',
      eyebrow: '',
      heading: '',
      body: '',
      product: 'Slide 2'
    },
    {
      type: 'product',
      chapter: '',
      bg: 'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/PitchDesk-Wedding_Presentation3.jpg?v=1777829978',
      bgPosition: 'center center',
      eyebrow: '',
      heading: '',
      body: '',
      product: 'Slide 3'
    },
    {
      type: 'product',
      chapter: '',
      bg: 'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/PitchDesk-Wedding_Presentation4.jpg?v=1777829979',
      bgPosition: 'center center',
      eyebrow: '',
      heading: '',
      body: '',
      product: 'Slide 4'
    },
    {
      type: 'product',
      chapter: '',
      bg: 'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/PitchDesk-Wedding_Presentation5.jpg?v=1777829979',
      bgPosition: 'center center',
      eyebrow: '',
      heading: '',
      body: '',
      product: 'Slide 5'
    },
    {
      type: 'product',
      chapter: '',
      bg: 'https://cdn.shopify.com/s/files/1/0984/3464/6308/files/PitchDesk-Wedding_Presentation6.jpg?v=1777829978',
      bgPosition: 'center center',
      eyebrow: '',
      heading: '',
      body: '',
      product: 'Slide 6'
    }
  ];

  /* ============================================================
     WEB COMPONENT
  ============================================================ */
  class PitchDeckCarousel extends HTMLElement {
    constructor () {
      super();
      this._currentIndex = 0;
      this._totalSlides  = SLIDES.length;
      this._autoTimer    = null;
      this._isPaused     = false;
      this._touchStartX  = 0;
      this._touchStartY  = 0;
      this._dragOffset   = 0;
      this._isDragging   = false;
      this._mouseDown    = false;
      this._mouseStartX  = 0;
      this._idleTimer    = null;
      this._helpVisible  = false;
      this._helpDismiss  = null;
      this._holdTimer    = null;
    }

    connectedCallback () {
      /* ── Mode detection ──────────────────────────────────────── */
      const params = new URLSearchParams(window.location.search);
      this._isExpoMode = params.get('display') === 'pitch';

      /* Find scoped DOM elements within this component */
      this._root     = this;
      this._carousel = this.querySelector('.lkbk-carousel');
      this._strip    = this.querySelector('.lkbk-progress-strip');
      this._chapter  = this.querySelector('.lkbk-chapter-label');
      this._counter  = this.querySelector('.lkbk-slide-counter');
      this._navPrev  = this.querySelector('.lkbk-nav-prev');
      this._navNext  = this.querySelector('.lkbk-nav-next');
      this._loading  = this.querySelector('.lkbk-loading');
      this._inner    = this.querySelector('.lkbk-inner');
      this._helpBtn  = this.querySelector('.lkbk-help-btn');
      this._helpTip  = this.querySelector('.lkbk-help-tooltip');
      this._restart  = this.querySelector('.lkbk-restart-btn');

      /* Apply initial mode class */
      if (this._isExpoMode) {
        this._applyFullscreenMode();
      } else {
        this.classList.add('is-embedded');
        /* Mark hosting section for CSS scoping */
        const section = this.closest('.shopify-section');
        if (section) section.classList.add('lkbk-hosting-section');
      }

      /* Set CSS duration variable — slower than lookbook since slides
         are presentation copy that visitors need time to read. */
      this.style.setProperty('--lkbk-duration-css', '9s');

      /* Initial fit mode — "contain" by default; remember the user's choice
         across reloads via localStorage. */
      const savedFit = (() => {
        try { return localStorage.getItem('pdck-fit'); } catch (e) { return null; }
      })();
      this._fitMode = (savedFit === 'cover') ? 'cover' : 'contain';
      this.classList.add('is-fit-' + this._fitMode);

      this._buildSlides();
      this._buildProgressBars();
      this._buildFullscreenButton();
      this._buildCloseButton();
      this._buildFitToggleButton();
      this._updateProgressBars(0);
      this._updateChapterLabel();
      this._updateCounter();
      this._updateRootClasses();
      this._bindEvents();

      /* In embedded mode, skip loading overlay and don't auto-advance */
      if (this._isExpoMode) {
        /* Preload first 3 images only, then reveal */
        const preloadCount = Math.min(3, SLIDES.length);
        let loaded = 0;
        const onLoad = () => {
          loaded++;
          if (loaded >= preloadCount) {
            if (this._loading) {
              this._loading.classList.add('fade-out');
              setTimeout(() => {
                if (this._loading) this._loading.style.display = 'none';
              }, 700);
            }
            this._startAutoAdvance();
          }
        };
        for (let i = 0; i < preloadCount; i++) {
          const img = new Image();
          img.onload  = onLoad;
          img.onerror = onLoad;
          img.src     = SLIDES[i].bg;
        }
      }
      /* Embedded mode: no loading overlay, no auto-advance on init */

      /* Background lazy-load of remaining images after window load */
      const lazyLoad = () => {
        SLIDES.slice(3).forEach(s => {
          if (!s.bg) return;
          const img = new Image();
          img.src = s.bg;
        });
      };
      if (document.readyState === 'complete') {
        setTimeout(lazyLoad, 1500);
      } else {
        window.addEventListener('load', () => setTimeout(lazyLoad, 1500), { once: true });
      }
    }

    /* ── FULLSCREEN BUTTON ──────────────────────────────────── */

    _buildFullscreenButton () {
      const btn = document.createElement('button');
      btn.className   = 'lkbk-fullscreen-btn';
      btn.type        = 'button';
      btn.setAttribute('aria-label', 'View pitch deck fullscreen');
      btn.innerHTML   = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
          stroke-linejoin="round" aria-hidden="true">
          <polyline points="15 3 21 3 21 9"></polyline>
          <polyline points="9 21 3 21 3 15"></polyline>
          <line x1="21" y1="3" x2="14" y2="10"></line>
          <line x1="3" y1="21" x2="10" y2="14"></line>
        </svg>
        Fullscreen`;
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const url = new URL(window.location.href);
        url.searchParams.set('display', 'pitch');
        window.location.href = url.toString();
      });
      this.appendChild(btn);
      this._fullscreenBtn = btn;
    }

    /* ── CLOSE BUTTON (fullscreen mode) ────────────────────── */

    _buildCloseButton () {
      const btn = document.createElement('button');
      btn.className   = 'lkbk-close-btn';
      btn.type        = 'button';
      btn.setAttribute('aria-label', 'Exit fullscreen');
      btn.innerHTML   = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
          stroke-linejoin="round" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>`;
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._exitFullscreen();
      });
      this.appendChild(btn);
      this._closeBtn = btn;
    }

    /* ── FIT TOGGLE (4:3 contain ↔ 16:9 cover) ───────────────── */

    _buildFitToggleButton () {
      const btn = document.createElement('button');
      btn.className = 'pdck-fit-btn';
      btn.type = 'button';
      btn.setAttribute('aria-label', 'Toggle slide fit mode');
      this.appendChild(btn);
      this._fitBtn = btn;
      const updateLabel = () => {
        btn.textContent = this._fitMode === 'cover' ? 'Fit: 16:9' : 'Fit: 4:3';
      };
      updateLabel();
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._fitMode = this._fitMode === 'cover' ? 'contain' : 'cover';
        this.classList.toggle('is-fit-cover', this._fitMode === 'cover');
        this.classList.toggle('is-fit-contain', this._fitMode === 'contain');
        try { localStorage.setItem('pdck-fit', this._fitMode); } catch (err) { /* ignore */ }
        updateLabel();
      });
    }

    /* ── FULLSCREEN ENTER / EXIT ────────────────────────────── */

    _applyFullscreenMode () {
      this.classList.remove('is-embedded');
      this.classList.add('is-fullscreen');
      /* Reuse the lookbook's fullscreen body dataset so existing CSS rules
         (hide header, hide non-hosting sections, body overflow:hidden) apply. */
      document.body.dataset.lkbkMode = 'expo';
      document.body.style.overflow   = 'hidden';

      /* Request Fullscreen API (requires user gesture; call via button or auto) */
      const requestFs = () => {
        if (this.requestFullscreen) {
          this.requestFullscreen().catch(() => {/* silently fail */});
        } else if (this.webkitRequestFullscreen) {
          this.webkitRequestFullscreen();
        }
      };
      /* Attempt on user first interaction (since it requires gesture) */
      const onGesture = () => {
        requestFs();
        document.removeEventListener('click', onGesture, true);
        document.removeEventListener('touchend', onGesture, true);
      };
      document.addEventListener('click', onGesture, { once: true, capture: true });
      document.addEventListener('touchend', onGesture, { once: true, capture: true });

      /* Wake-lock: keep screen on during expo */
      if ('wakeLock' in navigator) {
        navigator.wakeLock.request('screen').catch(() => {/* HTTPS-only, ignore on dev */});
      }

      /* Mark hosting section */
      const section = this.closest('.shopify-section');
      if (section) section.classList.add('lkbk-hosting-section');

      /* Idle reset: after 30s of inactivity, return to slide 0 */
      this._bindIdleReset();
    }

    _enterFullscreen () {
      this._stopAutoAdvance();
      this._applyFullscreenMode();
      this._startAutoAdvance();
    }

    _exitFullscreen () {
      this.classList.remove('is-fullscreen');
      this.classList.add('is-embedded');
      delete document.body.dataset.lkbkMode;
      document.body.style.overflow = '';
      this._stopAutoAdvance();
      this._isExpoMode = false;

      /* Strip ?display=pitch from the URL so reloading or sharing the
         link doesn't snap the page back into fullscreen mode. Use
         replaceState so we don't add to history (no extra back-button entry). */
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.get('display') === 'pitch') {
          url.searchParams.delete('display');
          const newUrl = url.pathname + (url.search ? url.search : '') + url.hash;
          window.history.replaceState({}, '', newUrl);
        }
      } catch (e) { /* ignore */ }

      /* Release wake-lock */
      if (this._wakeLock) {
        this._wakeLock.release().catch(() => {});
        this._wakeLock = null;
      }

      /* Exit browser fullscreen if active */
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      }
    }

    /* ── IDLE RESET (expo / fullscreen mode only) ─────────────── */

    _bindIdleReset () {
      const IDLE_MS = 30000;
      const resetIdleTimer = () => {
        clearTimeout(this._expoIdleTimer);
        this._expoIdleTimer = setTimeout(() => {
          /* After 30s idle in expo mode, snap back to slide 0 */
          if (this.classList.contains('is-fullscreen')) {
            this._goTo(0);
            this._isPaused = false;
            this._startAutoAdvance();
          }
        }, IDLE_MS);
      };
      document.addEventListener('touchstart', resetIdleTimer, { passive: true });
      document.addEventListener('click', resetIdleTimer);
      document.addEventListener('mousemove', resetIdleTimer);
      resetIdleTimer();
    }

    disconnectedCallback () {
      this._stopAutoAdvance();
    }

    /* ── SLIDE BUILDING ─────────────────────────────────────── */

    _buildSlides () {
      SLIDES.forEach((data, i) => {
        const slide = document.createElement('div');
        slide.className = 'lkbk-slide' + (i === 0 ? ' active' : '');
        if (data.type === 'hook')    slide.classList.add('lkbk-slide--hook');
        if (data.type === 'cta')     slide.classList.add('lkbk-slide--cta');
        if (data.type === 'chapter') slide.classList.add('lkbk-slide--chapter');
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-label', 'Slide ' + (i + 1) + ' of ' + this._totalSlides);
        slide.dataset.index = i;

        /* Background
           Bug B fix: append a comment node so this div is not matched by
           the theme-wide `div:empty { display: none }` rule in base.css */
        const bg = document.createElement('div');
        bg.className = 'lkbk-slide__bg';
        bg.style.backgroundImage    = 'url(' + data.bg + ')';
        bg.style.backgroundPosition = data.bgPosition || 'center center';
        bg.appendChild(document.createComment(' image '));
        slide.appendChild(bg);

        /* Overlay — same empty-div workaround */
        const overlay = document.createElement('div');
        overlay.className = 'lkbk-slide__overlay';
        overlay.appendChild(document.createComment(' overlay '));
        slide.appendChild(overlay);

        /* Content */
        slide.appendChild(this._buildContent(data));

        this._carousel.appendChild(slide);
      });
    }

    _buildContent (data) {
      const c = document.createElement('div');
      c.className = 'lkbk-slide__content';

      if (data.type === 'hook') {
        /* Pride bar */
        const prideBar = document.createElement('div');
        prideBar.className = 'lkbk-pride-bar';
        prideBar.setAttribute('aria-hidden', 'true');
        ['#FF0018','#FFA52C','#FFFF41','#008018','#0000F9','#86007D'].forEach(col => {
          const s = document.createElement('span');
          s.style.background = col;
          prideBar.appendChild(s);
        });
        c.appendChild(prideBar);

        const wordmark = document.createElement('div');
        wordmark.className   = 'lkbk-brand-wordmark';
        wordmark.textContent = 'Midnight Muse Made';
        c.appendChild(wordmark);

        if (data.eyebrow) {
          const ey = document.createElement('span');
          ey.className   = 'lkbk-eyebrow';
          ey.textContent = data.eyebrow;
          c.appendChild(ey);
        }

        const h = document.createElement('h1');
        h.className = 'lkbk-heading';
        h.innerHTML = data.heading;
        c.appendChild(h);

        if (data.body) {
          const p = document.createElement('p');
          p.className   = 'lkbk-body';
          p.textContent = data.body;
          c.appendChild(p);
        }
        return c;
      }

      if (data.type === 'chapter') {
        const num = document.createElement('div');
        num.className   = 'lkbk-chapter-number';
        num.textContent = data.chapterNum;
        c.appendChild(num);

        const orn = document.createElement('div');
        orn.className = 'lkbk-chapter-ornament';
        orn.setAttribute('aria-hidden', 'true');
        orn.textContent = '✦';
        c.appendChild(orn);

        const h = document.createElement('h2');
        h.className = 'lkbk-heading';
        h.innerHTML = data.heading;
        c.appendChild(h);

        if (data.tagline) {
          const tl = document.createElement('p');
          tl.className   = 'lkbk-chapter-tagline';
          tl.textContent = data.tagline;
          c.appendChild(tl);
        }
        return c;
      }

      if (data.type === 'cta') {
        const h = document.createElement('h2');
        h.className = 'lkbk-heading';
        h.innerHTML = data.heading;
        c.appendChild(h);

        if (data.body) {
          const p = document.createElement('p');
          p.className = 'lkbk-body';
          p.innerHTML = data.body;
          c.appendChild(p);
        }

        /* Cards row */
        const cards = document.createElement('div');
        cards.className = 'lkbk-cta-cards';

        /* QR placeholder */
        const qr = document.createElement('div');
        qr.className = 'lkbk-qr';
        qr.setAttribute('aria-label', 'QR code — scan to visit the shop');
        qr.innerHTML = `
          <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <rect x="5"  y="5"  width="35" height="35" fill="none" stroke="#0d0a14" stroke-width="4"/>
            <rect x="13" y="13" width="19" height="19" fill="#0d0a14"/>
            <rect x="60" y="5"  width="35" height="35" fill="none" stroke="#0d0a14" stroke-width="4"/>
            <rect x="68" y="13" width="19" height="19" fill="#0d0a14"/>
            <rect x="5"  y="60" width="35" height="35" fill="none" stroke="#0d0a14" stroke-width="4"/>
            <rect x="13" y="68" width="19" height="19" fill="#0d0a14"/>
            <rect x="60" y="60" width="8"  height="8"  fill="#0d0a14"/>
            <rect x="72" y="60" width="8"  height="8"  fill="#0d0a14"/>
            <rect x="84" y="60" width="11" height="8"  fill="#0d0a14"/>
            <rect x="60" y="72" width="23" height="8"  fill="#0d0a14"/>
            <rect x="60" y="84" width="8"  height="11" fill="#0d0a14"/>
            <rect x="72" y="84" width="8"  height="11" fill="#0d0a14"/>
            <rect x="84" y="84" width="11" height="11" fill="#0d0a14"/>
            <rect x="5"  y="44" width="8"  height="8"  fill="#0d0a14"/>
            <rect x="17" y="44" width="8"  height="8"  fill="#0d0a14"/>
            <rect x="29" y="44" width="8"  height="8"  fill="#0d0a14"/>
            <rect x="44" y="5"  width="8"  height="8"  fill="#0d0a14"/>
            <rect x="44" y="17" width="8"  height="8"  fill="#0d0a14"/>
            <rect x="44" y="29" width="8"  height="8"  fill="#0d0a14"/>
            <rect x="44" y="44" width="8"  height="8"  fill="#0d0a14"/>
            <rect x="44" y="56" width="8"  height="8"  fill="#0d0a14"/>
          </svg>
          <span>Scan to shop</span>`;
        cards.appendChild(qr);

        const div = document.createElement('div');
        div.className = 'lkbk-cta-divider';
        div.setAttribute('aria-hidden', 'true');
        div.textContent = '— or —';
        cards.appendChild(div);

        /* Email form */
        const form = document.createElement('div');
        form.className = 'lkbk-form-card';
        form.innerHTML = `
          <p>Get our wedding lookbook &amp; a secret discount.</p>
          <input class="lkbk-form-input" type="email" placeholder="your@email.com"
            aria-label="Email address" autocomplete="email" inputmode="email" />
          <button class="lkbk-form-btn" type="button"
            aria-label="Join the coven and receive discount">Join the Coven</button>
          <div class="lkbk-form-success" role="status" aria-live="polite">
            ✦ You're in the coven. Check your inbox.
          </div>`;

        const btn     = form.querySelector('.lkbk-form-btn');
        const input   = form.querySelector('.lkbk-form-input');
        const success = form.querySelector('.lkbk-form-success');

        btn.addEventListener('click', () => {
          const val = input.value.trim();
          if (!val || !val.includes('@')) {
            input.focus();
            input.style.borderColor = '#e05c5c';
            setTimeout(() => { input.style.borderColor = ''; }, 1500);
            return;
          }
          /* In production: POST to email service or Shopify customer endpoint */
          console.log('[Lookbook] Email captured:', val);
          input.style.display   = 'none';
          btn.style.display     = 'none';
          success.style.display = 'block';
        });

        cards.appendChild(form);
        c.appendChild(cards);

        /* Restart button — visible only on last slide via CSS .is-last rule */
        const restartBtn = this._restart;
        if (restartBtn) {
          c.appendChild(restartBtn.cloneNode(true));
          /* Wire the cloned restart; the original in DOM is hidden */
        }

        return c;
      }

      /* Default: product slide */
      if (data.eyebrow) {
        const ey = document.createElement('span');
        ey.className   = 'lkbk-eyebrow';
        ey.textContent = data.eyebrow;
        c.appendChild(ey);
      }

      const h = document.createElement('h2');
      h.className = 'lkbk-heading';
      h.innerHTML = data.heading;
      c.appendChild(h);

      if (data.body) {
        const p = document.createElement('p');
        p.className = 'lkbk-body';
        /* Use innerHTML so <em> highlights from blurb fields render correctly */
        p.innerHTML = data.body;
        c.appendChild(p);
      }

      if (data.product) {
        const pn = document.createElement('span');
        pn.className   = 'lkbk-product-name';
        pn.textContent = data.product;
        c.appendChild(pn);
      }

      return c;
    }

    /* ── PROGRESS BARS ────────────────────────────────────────── */

    _buildProgressBars () {
      this._strip.innerHTML = '';
      for (let i = 0; i < this._totalSlides; i++) {
        const seg  = document.createElement('div');
        seg.className    = 'lkbk-progress-seg';
        seg.dataset.index = i;
        const fill = document.createElement('div');
        fill.className   = 'lkbk-fill';
        seg.appendChild(fill);
        this._strip.appendChild(seg);
      }
    }

    _updateProgressBars (index) {
      const segs = this._strip.querySelectorAll('.lkbk-progress-seg');
      segs.forEach((seg, i) => {
        seg.classList.remove('done', 'active');
        const fill = seg.querySelector('.lkbk-fill');
        fill.style.animation = 'none';
        void fill.offsetWidth; /* force reflow to restart animation */
        if (i < index) {
          seg.classList.add('done');
        } else if (i === index) {
          seg.classList.add('active');
          fill.style.animation = '';
        }
      });
    }

    /* ── NAVIGATION ───────────────────────────────────────────── */

    _goTo (index) {
      if (index < 0 || index >= this._totalSlides) return;

      const slides = this._carousel.querySelectorAll('.lkbk-slide');
      slides[this._currentIndex].classList.remove('active');
      this._currentIndex = index;
      slides[this._currentIndex].classList.add('active');

      this._carousel.style.transform = 'translateX(-' + (this._currentIndex * 100) + '%)';

      this._updateProgressBars(this._currentIndex);
      this._updateChapterLabel();
      this._updateCounter();
      this._updateRootClasses();
      this._resetAutoAdvance();
    }

    _goNext () {
      if (this._currentIndex < this._totalSlides - 1) {
        this._goTo(this._currentIndex + 1);
      } else {
        /* Swipe forward from last slide wraps to first */
        this._goTo(0);
      }
    }

    _goPrev () {
      this._goTo(Math.max(this._currentIndex - 1, 0));
    }

    _updateChapterLabel () {
      const chapter = SLIDES[this._currentIndex].chapter;
      if (chapter && CHAPTERS[chapter]) {
        this._chapter.textContent  = CHAPTERS[chapter];
        this._chapter.style.opacity = '1';
      } else {
        this._chapter.style.opacity = '0';
      }
    }

    _updateCounter () {
      this._counter.textContent = (this._currentIndex + 1) + ' / ' + this._totalSlides;
    }

    _updateRootClasses () {
      this._root.classList.toggle('is-first', this._currentIndex === 0);
      this._root.classList.toggle('is-last',  this._currentIndex === this._totalSlides - 1);
    }

    /* ── AUTO-ADVANCE (with loop) ─────────────────────────────── */

    _startAutoAdvance () {
      this._stopAutoAdvance();
      if (this._isPaused) return;
      /* Auto-advance only runs in fullscreen/expo mode */
      if (!this.classList.contains('is-fullscreen')) return;

      /* Auto-advance loops back to slide 0 after the last (CTA) slide */
      this._autoTimer = setTimeout(() => {
        if (this._currentIndex >= this._totalSlides - 1) {
          this._goTo(0); /* loop */
        } else {
          this._goNext();
        }
      }, 9000);
    }

    _stopAutoAdvance () {
      if (this._autoTimer) {
        clearTimeout(this._autoTimer);
        this._autoTimer = null;
      }
    }

    _resetAutoAdvance () {
      this._stopAutoAdvance();
      this._startAutoAdvance();
    }

    /* ── EVENT BINDING ────────────────────────────────────────── */

    _bindEvents () {
      /* Touch swipe */
      this._carousel.addEventListener('touchstart', (e) => {
        this._touchStartX = e.touches[0].clientX;
        this._touchStartY = e.touches[0].clientY;
        this._dragOffset  = 0;
        this._isDragging  = false;
        this._stopAutoAdvance();
        /* Hold-to-pause */
        this._holdTimer = setTimeout(() => {
          this._isPaused = true;
          this._stopAutoAdvance();
        }, 200);
      }, { passive: true });

      this._carousel.addEventListener('touchmove', (e) => {
        const dx = e.touches[0].clientX - this._touchStartX;
        const dy = e.touches[0].clientY - this._touchStartY;
        if (!this._isDragging && Math.abs(dy) > Math.abs(dx)) return;
        this._isDragging = true;
        this._dragOffset = dx;
        const base = -(this._currentIndex * 100);
        this._carousel.classList.add('dragging');
        this._carousel.style.transform =
          'translateX(calc(' + base + '% + ' + this._dragOffset + 'px))';
      }, { passive: true });

      this._carousel.addEventListener('touchend', () => {
        clearTimeout(this._holdTimer);
        this._carousel.classList.remove('dragging');
        if (this._isDragging && Math.abs(this._dragOffset) > 50) {
          if (this._dragOffset < 0) this._goNext();
          else                      this._goPrev();
        } else {
          this._carousel.style.transform =
            'translateX(-' + (this._currentIndex * 100) + '%)';
          this._isPaused = false;
          this._startAutoAdvance();
        }
        this._isDragging = false;
        this._dragOffset = 0;
      });

      /* Mouse drag (desktop testing) */
      this._carousel.addEventListener('mousedown', (e) => {
        this._mouseDown   = true;
        this._mouseStartX = e.clientX;
        this._dragOffset  = 0;
        this._stopAutoAdvance();
      });

      window.addEventListener('mousemove', (e) => {
        if (!this._mouseDown) return;
        this._dragOffset = e.clientX - this._mouseStartX;
        const base = -(this._currentIndex * 100);
        this._carousel.classList.add('dragging');
        this._carousel.style.transform =
          'translateX(calc(' + base + '% + ' + this._dragOffset + 'px))';
      });

      window.addEventListener('mouseup', () => {
        if (!this._mouseDown) return;
        this._mouseDown = false;
        this._carousel.classList.remove('dragging');
        if (Math.abs(this._dragOffset) > 50) {
          if (this._dragOffset < 0) this._goNext();
          else                      this._goPrev();
        } else {
          this._carousel.style.transform =
            'translateX(-' + (this._currentIndex * 100) + '%)';
          this._startAutoAdvance();
        }
      });

      /* Nav tap zones */
      this._navPrev.addEventListener('click', (e) => {
        e.stopPropagation();
        this._goPrev();
      });
      this._navNext.addEventListener('click', (e) => {
        e.stopPropagation();
        this._goNext();
      });

      this._navPrev.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._goPrev(); }
      });
      this._navNext.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._goNext(); }
      });

      /* Keyboard navigation */
      document.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft')  this._goPrev();
        if (e.key === 'ArrowRight') this._goNext();
        if (e.key === ' ') {
          e.preventDefault();
          this._isPaused = !this._isPaused;
          this._isPaused ? this._stopAutoAdvance() : this._startAutoAdvance();
        }
        /* Esc exits fullscreen mode */
        if (e.key === 'Escape' && this.classList.contains('is-fullscreen')) {
          this._exitFullscreen();
        }
      });

      /* Tap anywhere in fullscreen briefly reveals the close button */
      this.addEventListener('click', (e) => {
        if (!this.classList.contains('is-fullscreen')) return;
        if (e.target.closest('.lkbk-close-btn')) return;
        if (e.target.closest('.lkbk-restart-btn')) return;
        if (e.target.closest('.lkbk-help-btn')) return;
        if (!this._closeBtn) return;
        this._closeBtn.classList.add('close-visible');
        clearTimeout(this._closeBtnTimer);
        this._closeBtnTimer = setTimeout(() => {
          if (this._closeBtn) this._closeBtn.classList.remove('close-visible');
        }, 3000);
      });

      /* Restart button — wired via event delegation on the root */
      this._root.addEventListener('click', (e) => {
        if (e.target.closest('.lkbk-restart-btn')) {
          this._goTo(0);
        }
      });

      /* Help tooltip */
      if (this._helpBtn && this._helpTip) {
        this._helpBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this._helpVisible = !this._helpVisible;
          this._helpTip.classList.toggle('visible', this._helpVisible);
          this._helpBtn.setAttribute('aria-expanded', String(this._helpVisible));
          this._helpTip.setAttribute('aria-hidden', String(!this._helpVisible));

          if (this._helpVisible) {
            this._stopAutoAdvance();
            clearTimeout(this._helpDismiss);
            this._helpDismiss = setTimeout(() => {
              this._helpVisible = false;
              this._helpTip.classList.remove('visible');
              this._helpBtn.setAttribute('aria-expanded', 'false');
              this._helpTip.setAttribute('aria-hidden', 'true');
              this._startAutoAdvance();
            }, 5000);
          } else {
            clearTimeout(this._helpDismiss);
            this._startAutoAdvance();
          }
        });

        document.addEventListener('click', (e) => {
          if (this._helpVisible &&
              !this._helpTip.contains(e.target) &&
              e.target !== this._helpBtn) {
            this._helpVisible = false;
            this._helpTip.classList.remove('visible');
            this._helpBtn.setAttribute('aria-expanded', 'false');
            this._helpTip.setAttribute('aria-hidden', 'true');
            clearTimeout(this._helpDismiss);
            this._startAutoAdvance();
          }
        });
      }

      /* Idle detection — show chevrons after 2s without interaction */
      const resetIdle = () => {
        if (this._inner) {
          this._inner.classList.remove('nav-visible');
          clearTimeout(this._idleTimer);
          this._idleTimer = setTimeout(() => {
            if (this._inner) this._inner.classList.add('nav-visible');
          }, 2000);
        }
      };
      document.addEventListener('touchstart', resetIdle, { passive: true });
      document.addEventListener('mousemove', resetIdle);
    }
  }

  /* Register only once (HMR / theme editor re-renders guard) */
  if (!customElements.get('pitch-deck-carousel')) {
    customElements.define('pitch-deck-carousel', PitchDeckCarousel);
  }

}());
